import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { InputSelect } from "components/Form/InputSelect";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateRewardShipment } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  rewardShipmentId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  trackingNumber: "",
  service: "",
  trackingLink: "",
};

const RewardShipmentFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  rewardShipmentId,
}) => {
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;
  // const [status, setStatus] = useState<ValueSchema | null>(null);
  const [service, setService] = useState<ValueSchema | null>(null);

  const { isFormLoading, rewardShipments } = useSelector(
    (state: any) => state.rewardShipmentReducer
  );

  // const [statusItems] = React.useState([
  //   { label: "Pending", value: "pending" },
  //   { label: "In Transit", value: "in_transit" },
  //   { label: "Delivered", value: "delivered" },
  //   { label: "Failed", value: "failed" },
  // ]);

  const [serviceItems] = React.useState([
    { label: "JNE", value: "JNE" },
    { label: "J&T", value: "J&T" },
    { label: "SiCepat", value: "SiCepat" },
    { label: "AnterAja", value: "AnterAja" },
    { label: "SAP Express", value: "SAP Express" },
  ]);

  const handleCloseModal = () => {
    onClose && onClose();
    // setStatus(null);
    setService(null);
  };

  const handleSubmit = (values: any) => {
    const {
      trackingNumber,
      // status,
      service,
      trackingLink,
    } = values;

    dispatch(
      updateRewardShipment(
        rewardShipmentId,
        {
          trackingNumber,
          // status,
          service,
          trackingLink,
        },
        handleCloseModal
      ) as any
    );
  };

  useEffect(() => {
    const rewardShipmentDetail = rewardShipments.find(
      (rewardShipment: any) => rewardShipment.id === parseInt(rewardShipmentId)
    );

    if (rewardShipmentDetail) {
      if (formikRef.current) {
        formikRef.current.setFieldValue(
          "trackingNumber",
          rewardShipmentDetail.tracking_number ?? ""
        );
        formikRef.current.setFieldValue(
          "service",
          rewardShipmentDetail.service
        );
        formikRef.current.setFieldValue(
          "trackingLink",
          rewardShipmentDetail.tracking_link ?? ""
        );
        // formikRef.current.setFieldValue("status", rewardShipmentDetail.status);

        serviceItems.map((item) =>
          item.value.toString() === rewardShipmentDetail.service
            ? setService(item)
            : null
        );
        // statusItems.map((item) =>
        //   item.value.toString() === rewardShipmentDetail.status
        //     ? setStatus(item)
        //     : null
        // );
      }
    }
  }, [rewardShipments, rewardShipmentId]);

  const validationSchema = yup.object({
    trackingNumber: yup.string().required("Tracking Number is required field"),
    service: yup.string().required("Service is required field"),
    // status: yup.string().required("Status is required field"),
    trackingLink: yup.string().required("Tracking Link is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>Update Reward Shipment</Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="trackingNumber">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="trackingNumber">
                          Tracking Number
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="service">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="service">Service</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputSelect
                            selectedItem={service}
                            width={"100%"}
                            placeholder="Select Service"
                            handleSelectedItemChange={({ selectedItem }) => {
                              if (selectedItem) {
                                setService(selectedItem);
                                setFieldValue("service", selectedItem.value);
                              }
                            }}
                            itemToString={(item) =>
                              item ? `${item.label}` : ""
                            }
                            itemValue={(item) => (item ? `${item.value}` : "")}
                            items={serviceItems}
                            size={"lg"}
                            borderRadius={"6px"}
                            errors={!!meta.error && meta.touched}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="trackingLink">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="trackingLink">
                          Tracking Link
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  {/* <Field name="status">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="status">Status</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputSelect
                            selectedItem={status}
                            width={"100%"}
                            placeholder="Select Status"
                            handleSelectedItemChange={({ selectedItem }) => {
                              if (selectedItem) {
                                setStatus(selectedItem);
                                setFieldValue("status", selectedItem.value);
                              }
                            }}
                            itemToString={(item) =>
                              item ? `${item.label}` : ""
                            }
                            itemValue={(item) => (item ? `${item.value}` : "")}
                            items={statusItems}
                            size={"lg"}
                            borderRadius={"6px"}
                            errors={!!meta.error && meta.touched}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field> */}
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    Update
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default RewardShipmentFormModal;
